import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';
import Card from './ui/Card';
import Modal from './ui/Modal';

const ULTRA_ADMIN_UID = 'ADA5WZOA5UNIai1WaE52S33DvX73';

function UltraAdminPage() {
    const { user, signOut } = useAuth();
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [institutionToDelete, setInstitutionToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [adminsModalOpen, setAdminsModalOpen] = useState(false);
    const [adminsLoading, setAdminsLoading] = useState(false);
    const [adminsError, setAdminsError] = useState(null);
    const [selectedInstitution, setSelectedInstitution] = useState(null);
    const [institutionAdmins, setInstitutionAdmins] = useState([]);
    const [institutionInvitations, setInstitutionInvitations] = useState([]);

    // Fetch all institutions from the ultra-admin endpoint
    const fetchInstitutions = async () => {
        if (!user || user.uid !== ULTRA_ADMIN_UID) {
            setError('Unauthorized: Only ultra-admin can view institutions');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            console.log('[UltraAdmin] Fetching institutions from ultra-admin endpoint...');

            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
            const apiUrl = `${baseUrl}/ultra-admin/institutions`;
            console.log('[UltraAdmin] API URL:', apiUrl);

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                    'Content-Type': 'application/json',
                },
            });
            console.log('[UltraAdmin] Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[UltraAdmin] Response error:', errorText);
                throw new Error(`Failed to fetch institutions: ${response.status} - ${errorText}`);
            }

            const data = await response.json();
            console.log('[UltraAdmin] Response data:', data);

            if (data.institutions) {
                console.log('[UltraAdmin] Found institutions:', data.institutions);
                setInstitutions(data.institutions);
            } else {
                console.error('[UltraAdmin] No institutions in response:', data);
                throw new Error('No institutions data in response');
            }
        } catch (err) {
            console.error('[UltraAdmin] Error fetching institutions:', err);
            setError(`Failed to load institutions: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Fetch admins for a specific institution (ultra-admin endpoint)
    const fetchInstitutionAdmins = async (institution) => {
        if (!user || user.uid !== ULTRA_ADMIN_UID) {
            setAdminsError('Unauthorized: Only ultra-admin can view admins');
            return;
        }
        try {
            setAdminsLoading(true);
            setAdminsError(null);
            setSelectedInstitution(institution);

            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
            const url = `${baseUrl}/ultra-admin/institutions/${institution.id}/admins`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || `Failed to fetch admins (${response.status})`);
            }
            const data = await response.json();
            setInstitutionAdmins(data.admins || []);
            setInstitutionInvitations(data.invitations || []);
            setAdminsModalOpen(true);
        } catch (err) {
            console.error('[UltraAdmin] fetchInstitutionAdmins error:', err);
            setAdminsError(err.message);
        } finally {
            setAdminsLoading(false);
        }
    };

    // Delete institution (this will need a special backend endpoint)
    const deleteInstitution = async (institutionId) => {
        if (!user || user.uid !== ULTRA_ADMIN_UID) {
            setError('Unauthorized: Only ultra-admin can delete institutions');
            return;
        }

        try {
            setDeleteLoading(true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
            const response = await fetch(
                `${baseUrl}/ultra-admin/institutions/${institutionId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${user.token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Delete failed' }));
                throw new Error(errorData.message || `Delete failed: ${response.status}`);
            }

            // Remove from local state
            setInstitutions(prev => prev.filter(inst => inst.id !== institutionId));
            setDeleteModalOpen(false);
            setInstitutionToDelete(null);
        } catch (err) {
            console.error('Error deleting institution:', err);
            setError(`Failed to delete institution: ${err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteClick = (institution) => {
        setInstitutionToDelete(institution);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (institutionToDelete) {
            deleteInstitution(institutionToDelete.id);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        if (user && user.uid === ULTRA_ADMIN_UID) {
            fetchInstitutions();
        }
    }, [user]);

    // Security check - only allow ultra admin
    if (!user || user.uid !== ULTRA_ADMIN_UID) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <Card className="p-8 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-4">Access Denied</h2>
                    <p className="text-gray-600 mb-4">You do not have permission to access this interface.</p>
                    <Button onClick={handleLogout} variant="secondary">
                        Sign Out
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <header className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Ultra Admin Dashboard</h1>
                            <p className="text-sm text-gray-500">Database Management Interface</p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-600">
                                {user.email} (Ultra Admin)
                            </span>
                            <Button onClick={handleLogout} variant="secondary" size="sm">
                                Sign Out
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                <div className="px-4 py-6 sm:px-0">
                    {/* Error Alert */}
                    {error && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                                <div className="ml-auto pl-3">
                                    <button
                                        onClick={() => setError(null)}
                                        className="inline-flex text-red-400 hover:text-red-600"
                                    >
                                        <span className="sr-only">Dismiss</span>
                                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Institutions Section */}
                    <Card>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-lg font-medium text-gray-900">Institution Management</h2>
                                    <p className="text-sm text-gray-500">
                                        Manage all institutions in the database
                                    </p>
                                </div>
                                <Button
                                    onClick={fetchInstitutions}
                                    variant="secondary"
                                    size="sm"
                                    disabled={loading}
                                >
                                    {loading ? 'Refreshing...' : 'Refresh'}
                                </Button>
                            </div>

                            {loading ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500">Loading institutions...</p>
                                </div>
                            ) : institutions.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500">No institutions found.</p>
                                </div>
                            ) : (
                                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    ID
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Name
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    URL Endpoint
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Admins
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {institutions.map((institution) => (
                                                <tr key={institution.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {institution.id}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {institution.name}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                                            {institution.urlEndpoint}
                                                        </code>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            onClick={() => fetchInstitutionAdmins(institution)}
                                                        >
                                                            View Admins
                                                        </Button>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <Button
                                                            onClick={() => handleDeleteClick(institution)}
                                                            variant="danger"
                                                            size="sm"
                                                            className="ml-2"
                                                        >
                                                            Delete
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </main>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setInstitutionToDelete(null);
                }}
                title="Delete Institution"
            >
                <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    Warning: This action cannot be undone!
                                </h3>
                                <div className="mt-2 text-sm text-red-700">
                                    <p>
                                        You are about to permanently delete the institution "
                                        <strong>{institutionToDelete?.name}</strong>" and ALL associated data including:
                                    </p>
                                    <ul className="mt-2 ml-4 list-disc">
                                        <li>All users belonging to this institution</li>
                                        <li>All teachers, students, groups, and subjects</li>
                                        <li>All schedule entries and preferences</li>
                                        <li>All rooms and academic year data</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {institutionToDelete && (
                        <div className="bg-gray-50 p-4 rounded-md">
                            <h4 className="font-medium text-gray-900 mb-2">Institution Details:</h4>
                            <dl className="text-sm">
                                <div className="flex justify-between py-1">
                                    <dt className="text-gray-600">ID:</dt>
                                    <dd className="font-mono">{institutionToDelete.id}</dd>
                                </div>
                                <div className="flex justify-between py-1">
                                    <dt className="text-gray-600">Name:</dt>
                                    <dd>{institutionToDelete.name}</dd>
                                </div>
                                <div className="flex justify-between py-1">
                                    <dt className="text-gray-600">URL Endpoint:</dt>
                                    <dd className="font-mono">{institutionToDelete.urlEndpoint}</dd>
                                </div>
                            </dl>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            onClick={() => {
                                setDeleteModalOpen(false);
                                setInstitutionToDelete(null);
                            }}
                            variant="secondary"
                            disabled={deleteLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteConfirm}
                            variant="danger"
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? 'Deleting...' : 'Delete Institution'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Admins Modal */}
            <Modal
                isOpen={adminsModalOpen}
                onClose={() => {
                    setAdminsModalOpen(false);
                    setInstitutionAdmins([]);
                    setInstitutionInvitations([]);
                    setSelectedInstitution(null);
                    setAdminsError(null);
                }}
                title={selectedInstitution ? `Admins for ${selectedInstitution.name}` : 'Admins'}
            >
                <div className="space-y-4">
                    {adminsLoading && (
                        <div className="text-center py-4 text-gray-600">Loading...</div>
                    )}
                    {adminsError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{adminsError}</div>
                    )}

                    {!adminsLoading && !adminsError && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Admins</h4>
                                {institutionAdmins.length === 0 ? (
                                    <p className="text-sm text-gray-500">No admins found.</p>
                                ) : (
                                    <ul className="divide-y divide-gray-200 border rounded-md">
                                        {institutionAdmins.map(a => (
                                            <li key={a.id} className="px-3 py-2 text-sm flex justify-between">
                                                <span className="text-gray-800">{a.name || a.email}</span>
                                                <span className="text-gray-500">{a.email}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Pending Invitations</h4>
                                {institutionInvitations.length === 0 ? (
                                    <p className="text-sm text-gray-500">No invitations found.</p>
                                ) : (
                                    <ul className="divide-y divide-gray-200 border rounded-md">
                                        {institutionInvitations.map(inv => (
                                            <li key={inv.id} className="px-3 py-2 text-sm">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="text-gray-800">{inv.email}</div>
                                                        <div className="text-xs text-gray-500">Role: {inv.role} • Status: {inv.status}</div>
                                                    </div>
                                                    {inv.expires_at && (
                                                        <span className="text-xs text-gray-400">Expires: {new Date(inv.expires_at).toLocaleDateString()}</span>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        <Button variant="secondary" onClick={() => setAdminsModalOpen(false)}>Close</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default UltraAdminPage; 